<template>
  <div class="flex items-start">
    <div class="relative flex-1">
      <div class="relative overflow-hidden rounded-lg border border-gray-300 shadow-xs">
        <div v-if="selectedFiles.length" class="flex flex-wrap gap-2 p-3">
          <div
            v-for="(file, index) in selectedFiles"
            :key="index"
            class="relative flex items-center gap-2 rounded-lg bg-white px-3 py-2"
            :class="[
              fileErrors.has(file) || invalidFiles.has(file)
                ? 'border border-red-500'
                : 'border border-gray-200',
            ]"
            data-cy="uploaded-file-item"
          >
            <icon-document class="flex-shrink-0" />
            <span class="text-sm break-all text-gray-700">{{ file.name }}</span>
            <button
              class="absolute -top-1 -right-1"
              data-cy="remove-file-button"
              @click="removeFile(index)"
            >
              <icon-remove-file />
            </button>
          </div>
        </div>

        <div class="relative flex items-center">
          <textarea
            ref="textareaRef"
            v-model="inputMessage"
            data-cy="chat-text-area"
            :placeholder="placeholder || t('chat.placeholder')"
            :disabled="disabled"
            :rows="defaultRows"
            class="hide-scrollbar max-h-[120px] w-full resize-none overflow-hidden border-none py-2 pr-12 pl-4 break-all text-heading focus:outline-none"
            @input="autoResize"
            @keydown.enter="handleKeyDown"
          ></textarea>
          <div class="absolute right-0 bottom-0 flex items-center space-x-1 pr-2">
            <button
              v-tooltip="t('common.attach')"
              class="p-2 text-gray-500 hover:text-blue-500 disabled:opacity-50 disabled:hover:text-gray-500"
              :disabled="disabled"
              data-cy="chat-attach-button"
              @click="() => open()"
            >
              <icon-attach />
            </button>
            <button
              class="p-2 text-gray-500 hover:text-blue-500 disabled:opacity-50 disabled:hover:text-gray-500"
              :disabled="!canSend || disabled || loading"
              data-cy="chat-send-button"
              @click="handleSend"
            >
              <div
                v-if="loading"
                class="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500"
              ></div>
              <icon-send v-else />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useFileDialog } from "@vueuse/core";
import { getChatFileAcceptString, isValidChatFileType, isValidChatFileSize } from "@/util/file";
import useToastState from "@/composables/state/toastState";
import IconSend from "@/components/icons/IconSend.vue";
import IconAttach from "@/components/icons/IconAttach.vue";
import IconDocument from "@/components/icons/IconDocument.vue";
import IconRemoveFile from "@/components/icons/IconRemoveFile.vue";

const BASE_TEXTAREA_HEIGHT = 24;
const LINE_HEIGHT = 20;
const RESIZE_DELAY = 0;

const { t } = useI18n();
const toast = useToastState();

const {
  disabled,
  placeholder = "",
  defaultRows = 1,
  maxRows = 5,
  loading = false,
  fileErrors = new Set(),
} = defineProps<{
  disabled?: boolean;
  placeholder?: string;
  defaultRows?: number;
  maxRows?: number;
  loading?: boolean;
  fileErrors?: Set<File>;
}>();

const emit = defineEmits<{
  send: [message: string, files: File[]];
  "file:selected": [files: FileList | null];
}>();

const inputMessage = ref("");
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const selectedFiles = ref<File[]>([]);
const invalidFiles = ref<Set<File>>(new Set());

const { open, onChange } = useFileDialog({
  accept: getChatFileAcceptString(),
  multiple: false,
});

const canSend = computed(() => {
  const validFilesCount = selectedFiles.value.filter(
    (file) => !invalidFiles.value.has(file)
  ).length;
  return !!inputMessage.value.trim().length || validFilesCount > 0;
});

onChange((files) => {
  if (files && files.length) {
    Array.from(files).forEach((file) => {
      const isValidType = isValidChatFileType(file);
      const isValidSize = isValidChatFileSize(file.size);

      if (!isValidType) {
        invalidFiles.value.add(file);
        toast.value.addMessage({
          type: "error",
          title: t("chat.FILE_UPLOAD.INVALID_TYPE", { fileName: file.name }),
        });
      }

      if (!isValidSize) {
        invalidFiles.value.add(file);
        toast.value.addMessage({
          type: "error",
          title: t("chat.FILE_UPLOAD.FILE_TOO_LARGE", { fileName: file.name }),
        });
      }

      selectedFiles.value.push(file);
    });
  }
  emit("file:selected", files);
});

const removeFile = (index: number) => {
  const file = selectedFiles.value[index];
  if (file) {
    invalidFiles.value.delete(file);
  }
  selectedFiles.value.splice(index, 1);
};

const autoResize = () => {
  if (!textareaRef.value) {
    return;
  }

  textareaRef.value.style.height = "auto";

  const newHeight = Math.min(
    textareaRef.value.scrollHeight,
    BASE_TEXTAREA_HEIGHT + (maxRows - 1) * LINE_HEIGHT
  );

  textareaRef.value.style.height = `${newHeight}px`;
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.shiftKey) {
    return;
  }
  event.preventDefault();
  handleSend();
};

const handleSend = () => {
  if (!canSend.value || disabled || loading) {
    return;
  }

  const validFiles = selectedFiles.value.filter((file) => !invalidFiles.value.has(file));
  emit("send", inputMessage.value, validFiles);
};

const clearInput = () => {
  inputMessage.value = "";
  selectedFiles.value = [];
  invalidFiles.value.clear();
  setTimeout(autoResize, RESIZE_DELAY);
};

defineExpose({
  clearInput,
});

onMounted(autoResize);
</script>

<style scoped>
@reference "@/tailwind.css";

.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
  overflow-y: auto;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
